import { z } from 'zod';
import { contentIdSchema } from '../rules/content-id.js';
import { journalStepSchema } from '../rules/journal.js';
import { contentBodySchema } from '../content/custom.js';
import { customTypeSchema } from '../content/form.js';
import { characterDetailsSchema, characterSummarySchema, EMPTY_DETAILS, } from './character.js';
/**
 * Файл персонажа (D54): журнал решений, а не посчитанный лист.
 *
 * Лист — свёртка журнала (D4), и в файле его нет намеренно. Числа,
 * записанные в файл, через полгода не с чем сверить: непонятно, КД 16
 * — это ошибка расчёта или правило, которое с тех пор поправили.
 * Журнал же пересчитывается движком по нынешнему контенту и даёт те же
 * числа, что видит остальной стол.
 *
 * Файл приходит снаружи — первые такие данные в проекте, — поэтому
 * схема здесь строгая во всех смыслах: `strictObject` отклоняет лишние
 * поля, идентификаторы строк и владельца файл не несёт вовсе,
 * а версия формата проверяется до разбора всего остального.
 */
/**
 * Версия формата файла. Число, а не строка: сравнивать «новее ли файл,
 * чем приложение» нужно порядком, а не равенством.
 */
export const CHARACTER_EXPORT_FORMAT = 1;
/**
 * Предел размера файла: 256 КиБ.
 *
 * Считается по тексту, который выбрал человек, а не по разобранному
 * объекту: разбирать мегабайты, чтобы потом отказать, — это работа,
 * которую делают за нас. Мерка взята с запасом от настоящего файла:
 * журнал двадцатого уровня со снаряжением плюс копия классовой записи
 * (самая большая в SRD — 26 КиБ) не доходят и до сотни.
 */
export const EXPORT_SIZE_LIMIT_BYTES = 256 * 1024;
/**
 * Запись пользовательского контента внутри файла (D53, D54).
 *
 * Тело целиком, а не ссылка: имена и числа должны быть в файле, иначе
 * тому, кто импортирует, нечего показать в списке «чего не хватает».
 * Идентификатор здесь — тот, что стоит в журнале; при импорте к себе
 * он меняется на собственный, а решение остаётся тем же.
 */
export const exportedContentSchema = z.strictObject({
    id: contentIdSchema,
    type: customTypeSchema,
    name: z.string().min(1),
    body: contentBodySchema,
});
export const characterExportSchema = z.strictObject({
    format: z.literal(CHARACTER_EXPORT_FORMAT),
    /** Версия приложения, которая писала файл: для разбора жалоб. */
    app: z.string().min(1),
    exportedAt: z.iso.datetime(),
    /**
     * Имя персонажа. Вместе с описанием — те поля строки, которые едут
     * в файл: из журнала они не выводятся (D4). Ни кода, ни
     * идентификатора, ни владельца здесь нет — их проставляет тот,
     * кто импортирует (D54).
     */
    name: z.string().min(1),
    /**
     * Описание персонажа: внешность, фишка, причина, прошлое, слабость,
     * заметки. Со значением по умолчанию — файл, написанный до этих
     * полей, остаётся читаемым и не требует второй версии формата.
     */
    details: characterDetailsSchema.default(EMPTY_DETAILS),
    journal: z.array(journalStepSchema).min(1),
    /** Записи редактора, на которые ссылается журнал; пусто — только SRD. */
    content: z.array(exportedContentSchema),
});
/**
 * Проба версии до разбора.
 *
 * Файл из будущей версии обязан получить внятный отказ, а не список
 * непонятных замечаний по полям, которых он и не обещал. Поэтому
 * сначала читается одно поле — свободной схемой, — и только потом
 * файл разбирается строгой.
 */
export const exportProbeSchema = z.looseObject({
    format: z.number().int().positive(),
});
export const exportCharacterInputSchema = z.strictObject({
    characterId: z.uuid(),
});
export const exportCharacterResultSchema = z.strictObject({
    /** Имя файла: человеку, а не машине — «Дрозд-2026-09-03.json». */
    fileName: z.string().min(1),
    file: characterExportSchema,
});
export const importCharacterInputSchema = z.strictObject({
    /** Содержимое файла как есть: разбирает его сервер, а не клиент. */
    file: z.string().min(1).max(EXPORT_SIZE_LIMIT_BYTES),
    /**
     * Согласие завести записи контента из файла как свои (D54).
     *
     * Отдельным полем, а не «само собой»: молчаливое создание чужих
     * записей — это и есть подстановка похожего контента, от которой
     * решение и защищает.
     */
    adoptContent: z.boolean(),
});
export const importProblemCodeSchema = z.enum([
    /** Файл больше предела. */
    'too-large',
    /** Не разбирается как JSON. */
    'not-json',
    /** Разбирается, но это не файл персонажа. */
    'bad-format',
    /** Файл писала версия новее нашей. */
    'future-format',
    /** Ссылка на запись, которой у импортирующего нет. */
    'missing-content',
    /** Ключ записи занят другой записью импортирующего. */
    'key-taken',
]);
export const importProblemSchema = z.strictObject({
    code: importProblemCodeSchema,
    /** Запись, о которой речь; `null` — отказ не про запись. */
    id: contentIdSchema.nullable(),
    /** Имя записи из файла: больше его взять неоткуда. */
    name: z.string().nullable(),
    /** Есть ли эта запись в файле: без неё «завести у себя» нечего. */
    inFile: z.boolean(),
    message: z.string().min(1),
});
export const importRejectionSchema = z.strictObject({
    problems: z.array(importProblemSchema).min(1),
});
export const importCharacterResultSchema = z.strictObject({
    character: characterSummarySchema,
    /** Сколько записей контента заведено у импортирующего. */
    adopted: z.number().int().nonnegative(),
});
//# sourceMappingURL=export.js.map